// src/pages/UserProfile.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import Page from "components/Page";
import SectionCard from "components/SectionCard";
import Subsection from "components/Subsection";
import { TextInput, DropdownSelect } from "components/InputStyled";

// Make the defaults stable so the effect deps are clean
const DEFAULT_FORM_STATE = {
  email: "",
  phone: "",
  first_name: "",
  last_name: "",
  company: "",
  is_financial_pro: false,
  address: "",
  city: "",
  state: "",
  zip_postcode: "",
};

export default function UserProfile() {
  const { profile, refreshProfile, loading } = useProfile();
  const navigate = useNavigate();

  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (profile) {
      setFormState({
        ...DEFAULT_FORM_STATE,
        ...profile,
        is_financial_pro: !!profile.is_financial_pro,
      });
    }
  }, [profile]);

  const setField = (name, value) => {
    setFormState((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!profile?.id) {
      setMessage("Error: No active profile found.");
      return;
    }

    const updateData = {
      ...formState,
      is_financial_pro: !!formState.is_financial_pro,
    };

    const { error } = await supabase
      .from("profiles")
      .update(updateData)
      .eq("id", profile.id);

    if (!error) {
      setMessage("Profile updated successfully.");
      if (typeof refreshProfile === "function") {
        await refreshProfile();
      }
    } else {
      setMessage("Error updating profile.");
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormState({
        ...DEFAULT_FORM_STATE,
        ...profile,
        is_financial_pro: !!profile.is_financial_pro,
      });
    }
  };

  if (loading) return <div className="p-6 text-muted">Loading profile…</div>;
  if (!profile) return <div className="p-6 text-muted">No profile found.</div>;

  return (
    <Page title="Your User Profile">
      <div className="max-w-3xl">
        {message && (
          <div className="mb-4 panel-subtle p-3 rounded text-sm">{message}</div>
        )}

        <SectionCard>
          <form onSubmit={handleSubmit} className="space-y-6">
            <Subsection title="Contact Information">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput
                  label="Email / Username"
                  type="email"
                  name="email"
                  value={formState.email}
                  disabled
                  className="w-full"
                />

                <TextInput
                  label="Phone"
                  name="phone"
                  value={formState.phone}
                  onChange={(value) => setField("phone", value)}
                  autoComplete="tel"
                  className="w-full"
                />
              </div>
            </Subsection>

            <Subsection title="Identity">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput
                  label="First Name"
                  name="first_name"
                  value={formState.first_name}
                  onChange={(value) => setField("first_name", value)}
                  required
                  autoComplete="given-name"
                  className="w-full"
                />

                <TextInput
                  label="Last Name"
                  name="last_name"
                  value={formState.last_name}
                  onChange={(value) => setField("last_name", value)}
                  required
                  autoComplete="family-name"
                  className="w-full"
                />

                <TextInput
                  label="Organization"
                  name="company"
                  value={formState.company}
                  onChange={(value) => setField("company", value)}
                  required
                  autoComplete="organization"
                  className="w-full"
                />

                <DropdownSelect
                  label="Are you a registered financial professional?"
                  name="is_financial_pro"
                  value={formState.is_financial_pro ? "yes" : "no"}
                  onChange={(value) =>
                    setField("is_financial_pro", value === "yes")
                  }
                  required
                  placeholder=""
                  className="w-full"
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ]}
                />
              </div>
            </Subsection>

            <Subsection title="Mailing Address">
              <div className="space-y-4">
                <TextInput
                  label="Street"
                  name="address"
                  value={formState.address}
                  onChange={(value) => setField("address", value)}
                  autoComplete="street-address"
                  className="w-full"
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <TextInput
                    label="City"
                    name="city"
                    value={formState.city}
                    onChange={(value) => setField("city", value)}
                    autoComplete="address-level2"
                    className="w-full"
                  />

                  <TextInput
                    label="State"
                    name="state"
                    value={formState.state}
                    onChange={(value) => setField("state", value)}
                    autoComplete="address-level1"
                    className="w-full"
                  />

                  <TextInput
                    label="ZIP / Postal Code"
                    name="zip_postcode"
                    value={formState.zip_postcode}
                    onChange={(value) => setField("zip_postcode", value)}
                    autoComplete="postal-code"
                    className="w-full"
                  />
                </div>
              </div>
            </Subsection>

            <div className="flex flex-wrap gap-4">
              <button type="submit" className="mt-2 btn btn-primary">
                Update Profile
              </button>

              <button
                type="button"
                onClick={() => navigate("/update-password")}
                className="mt-2 btn btn-primary"
              >
                Change Email or Password
              </button>

              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-ghost underline"
              >
                Cancel Changes
              </button>
            </div>
          </form>
        </SectionCard>
      </div>
    </Page>
  );
}